import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiArrowLeftLine, RiDownloadLine, RiBarChartBoxLine, RiTimeLine, RiGroupLine, RiAlertLine } from 'react-icons/ri';
import './AdminData.css';
import { checkAdminStatus } from '../../services/admin.service';

const AdminData = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportDays, setExportDays] = useState(30);
  const [exporting, setExporting] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);

  useEffect(() => {
    // Check admin status
    checkAdminStatus().then(isAdmin => {
      if (!isAdmin) {
        navigate('/profile');
        return;
      }
      fetchFacilities();
      fetchStats();
    }).catch(() => {
      navigate('/profile');
    });
  }, [navigate]);

  useEffect(() => {
    // Fetch stats when facility selection changes
    if (facilities.length > 0) {
      fetchStats();
    }
  }, [selectedFacility]);

  const fetchFacilities = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const token = user?.access_token;

      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch('https://campuslens.xyz/api/facilities', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFacilities(data.facilities || []);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const token = user?.access_token;

      if (!token) {
        navigate('/signin');
        return;
      }

      const url = selectedFacility
        ? `https://campuslens.xyz/api/admin/facility-stats?facility_id=${selectedFacility}`
        : 'https://campuslens.xyz/api/admin/facility-stats';

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        console.error('Failed to fetch stats');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const token = user?.access_token;

      if (!token) {
        navigate('/signin');
        return;
      }

      let url = `https://campuslens.xyz/api/admin/export-detections?format=csv&days=${exportDays}`;
      if (selectedFacility) {
        url += `&facility_id=${selectedFacility}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Download the file
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        const facilityName = selectedFacility ? `_facility${selectedFacility}` : '';
        a.download = `detection_data${facilityName}_${exportDays}days_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        const errorText = await response.text().catch(() => '');
        if (response.status === 500 || errorText.includes('no data') || errorText.includes('empty')) {
          alert('Export failed: No aggregated data available. The system needs at least 24 hours of detection data to generate analytics summaries. The background aggregation job may not be running. Please contact your system administrator.');
        } else {
          alert('Failed to export data: ' + (errorText || 'Server error'));
        }
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Export failed: Unable to connect to the server. Please try again later.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-data-page">
        <div className="admin-data-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-data-page">
      <div className="admin-data-container">
        <div className="admin-data-header">
          <button className="back-btn" onClick={() => navigate('/profile')}>
            <RiArrowLeftLine /> Back to Profile
          </button>
          <h1 className="admin-title">
            <RiBarChartBoxLine className="title-icon" />
            Admin Data & Statistics
          </h1>
        </div>

        <div className="facility-filter-section">
          <label htmlFor="facility-filter">Filter by Facility:</label>
          <select
            id="facility-filter"
            value={selectedFacility || ''}
            onChange={(e) => setSelectedFacility(e.target.value ? Number(e.target.value) : null)}
            className="facility-select"
          >
            <option value="">All Facilities</option>
            {facilities.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </div>

        {stats && stats.facilities && stats.facilities.map((facility) => (
          <div key={facility.facility_id} className="stats-section">
            <h2 className="facility-name">{facility.facility_name}</h2>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon current">
                  <RiGroupLine />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Current Occupancy</div>
                  <div className="stat-value">{facility.current_occupancy}</div>
                  <div className="stat-subtext">People currently detected</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon today">
                  <RiTimeLine />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Today's Detections</div>
                  <div className="stat-value">{facility.today_total_detections}</div>
                  <div className="stat-subtext">Total detections today</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon peak">
                  <RiBarChartBoxLine />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Peak Hour Today</div>
                  <div className="stat-value">{facility.peak_hour_today}</div>
                  <div className="stat-subtext">{facility.peak_count_today} people at peak</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon weekly">
                  <RiGroupLine />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Weekly Average</div>
                  <div className="stat-value">{facility.weekly_average}</div>
                  <div className="stat-subtext">Average detections/day</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon database">
                  <RiBarChartBoxLine />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Records</div>
                  <div className="stat-value">{facility.total_records.toLocaleString()}</div>
                  <div className="stat-subtext">Records in database</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon errors">
                  <RiAlertLine />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Detection Errors</div>
                  <div className="stat-value">{facility.error_count}</div>
                  <div className="stat-subtext">System errors logged</div>
                </div>
              </div>
            </div>

            <div className="stat-meta">
              Last updated: {new Date(facility.last_updated).toLocaleString()}
            </div>
          </div>
        ))}

        <div className="export-section">
          <h2 className="section-title">
            <RiDownloadLine className="title-icon" />
            Export Detection Data
          </h2>

          <div className="export-card">
            <div className="export-description">
              Download historical occupancy summary data as CSV for analysis.
              Data includes 15-minute aggregated occupancy metrics, averages, peaks, and total detections.
            </div>

            <div className="export-controls">
              <div className="export-option">
                <label htmlFor="export-facility-select">Facility:</label>
                <select
                  id="export-facility-select"
                  value={selectedFacility || ''}
                  onChange={(e) => setSelectedFacility(e.target.value ? Number(e.target.value) : null)}
                  className="facility-select"
                >
                  <option value="">All Facilities</option>
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="export-option">
                <label htmlFor="days-select">Time Range:</label>
                <select
                  id="days-select"
                  value={exportDays}
                  onChange={(e) => setExportDays(Number(e.target.value))}
                  className="days-select"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 180 days</option>
                  <option value={365}>Last 365 days</option>
                </select>
              </div>

              <button
                className="export-btn"
                onClick={handleExport}
                disabled={exporting}
              >
                <RiDownloadLine className="btn-icon" />
                {exporting ? 'Exporting...' : 'Export as CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminData;
